import type * as Y from "yjs";
import {
  deleteRow,
  deleteSection,
  listPanels,
  listRows,
  listSections,
  newPanel,
  newRow,
  newSection,
  type Panel,
  type PanelMaps,
  type PanelRow,
  patchPanel,
  putPanel,
  putRow,
  putSection,
  runLocal,
} from "@electro-planner/domain";
import {
  interpretCableCode,
  interpretComponentCode,
} from "@electro-planner/components";
import type { PanelTemplate, TemplateRow } from "./types.ts";

const PENDING_KEY = "electro-planner-panels:pending-template";

/**
 * Lagrar en mall-nyckel som ska appliceras på ett dokument-id nästa gång
 * dokumentet öppnas. Vi använder sessionStorage så mall-valet överlever
 * navigering (route-byte) men inte en stängd flik.
 */
export function setPendingTemplate(documentId: string, templateKey: string) {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(`${PENDING_KEY}:${documentId}`, templateKey);
}

export function takePendingTemplate(documentId: string): string | null {
  if (typeof sessionStorage === "undefined") return null;
  const key = `${PENDING_KEY}:${documentId}`;
  const val = sessionStorage.getItem(key);
  if (val) sessionStorage.removeItem(key);
  return val;
}

/**
 * Applicerar en mall på en (typiskt nyskapad) Y.Doc. Tömmer först eventuellt
 * befintligt innehåll, sedan:
 *   1. Panel-meta sätts
 *   2. Sektioner skapas i ordning
 *   3. Rader skapas, varje rad får först en shortCode-tolkning applicerad
 *      (componentCode / cableCode), sedan overrides från template-raden.
 */
export function applyTemplate(
  doc: Y.Doc,
  maps: PanelMaps,
  template: PanelTemplate,
): void {
  runLocal(doc, () => {
    // Töm existerande data först — mallen är en tabula rasa.
    for (const r of listRows(maps)) deleteRow(maps, r.id);
    for (const s of listSections(maps)) deleteSection(maps, s.id);

    // Sätt upp panel-meta. Om det redan finns en panel patchar vi, annars
    // skapar vi en ny (vilket är normala fallet för ett nytt dokument).
    const panels = listPanels(maps);
    if (panels.length > 0) {
      patchPanel(maps, panels[0]!.id, template.panel);
    } else {
      const panel: Panel = newPanel(template.panel);
      putPanel(maps, panel);
    }

    // Skapa sektioner, spara section-id per mall-kod så rader kan mappas.
    const sectionIdByCode = new Map<string, string>();
    template.sections.forEach((s, i) => {
      const section = newSection(s.code, i, s.label);
      putSection(maps, section);
      sectionIdByCode.set(s.code, section.id);
    });

    // Lägg till rader. Global ordning = iterations-ordning över sektioner.
    let globalOrder = 0;
    for (const s of template.sections) {
      const sectionId = sectionIdByCode.get(s.code);
      for (const tRow of s.rows) {
        const row = buildRowFromTemplate(tRow, globalOrder, sectionId);
        putRow(maps, row);
        globalOrder += 1;
      }
    }
  });
}

function buildRowFromTemplate(
  t: TemplateRow,
  order: number,
  sectionId: string | undefined,
): PanelRow {
  // Start from en tom rad med autogenerat id och explicit ordning.
  let row = newRow(order, t.designation ?? `F${order + 1}`);
  if (sectionId) {
    row = { ...row, sectionId: sectionId as PanelRow["sectionId"] };
  }

  // Steg 1: tolka componentCode via shortCode (t.ex. "DB16" → poles=1, char=B, A=16).
  if (t.componentCode) {
    const s = interpretComponentCode(t.componentCode);
    if (s) row = { ...row, ...s.patch };
  }
  // Steg 2: tolka cableCode (t.ex. "3G1,5" → cableType, cableCores, cableArea).
  if (t.cableCode) {
    const s = interpretCableCode(t.cableCode);
    if (s) row = { ...row, ...s.patch };
  }
  // Steg 3: explicita fält från mallen överskriver shortCode-tolkningen.
  const {
    componentCode: _cc,
    cableCode: _ccode,
    ...explicit
  } = t;
  void _cc;
  void _ccode;
  row = { ...row, ...explicit } as PanelRow;

  // Om mallen inte själv satte en designation men den generade F${order+1}
  // kolliderar med ett annat namn är det inte vårt problem här — lämnar.
  return row;
}
